import { sortBy } from 'lodash';
import moment from 'moment';

import {
  FamilyTimeSlot,
  IResolvedAvailability,
} from 'interfaces/familyBookingTypes';
import { ITimeSlot, SearchingService } from 'interfaces/timeslotType';
import getPractitionerName from 'utils/getPractitionerName';
import { getBlocksPerInterval } from 'utils/updatedSlotsPer30Minutes';

import { DATE_FORMAT, NEXT_AVAILABILITY_SEARCH_MONTHS } from '../constants';
import {
  INextAvailabilityWindow,
  IPractitionerOption,
  IServiceOption,
} from '../types';

export const getChildServiceId = ({
  serviceId,
  bookingServiceId,
  services,
  searchingService,
}: {
  serviceId: string;
  bookingServiceId?: string | null;
  services: IServiceOption[];
  searchingService: SearchingService;
}) => {
  const lookupServiceId = bookingServiceId || serviceId;

  const selectedService = services.find(
    (service) => service.id === lookupServiceId
  );

  const parentService = services.find((service) =>
    service.childServices?.some(
      (childService) => childService.id === lookupServiceId
    )
  );

  const childServices =
    selectedService?.childServices || parentService?.childServices;

  if (!childServices?.length) return null;

  return (
    childServices.find(
      (childService) => childService.id === searchingService.serviceId
    )?.id || (parentService ? lookupServiceId : null)
  );
};

export const getServiceDuration = ({
  serviceId,
  bookingServiceId,
  childServiceId,
  services,
}: {
  serviceId: string;
  bookingServiceId?: string | null;
  childServiceId: string;
  services: IServiceOption[];
}) => {
  const lookupServiceId = bookingServiceId || serviceId;

  const selectedService = services.find(
    (service) => service.id === lookupServiceId
  );

  const parentService = selectedService
    ? null
    : services.find((service) =>
        service.childServices?.some(
          (childService) => childService.id === lookupServiceId
        )
      );

  const serviceWithChildren = selectedService || parentService;

  const childService = serviceWithChildren?.childServices?.find(
    (service) => service.id === childServiceId
  );

  return childService?.duration || selectedService?.duration || 0;
};

export const isExamAndCleaningService = ({
  serviceId,
  services,
}: {
  serviceId: string;
  services: IServiceOption[];
}) =>
  services.some((service) => {
    const isServiceItself = service.id === serviceId;

    const isChildService = !!service.childServices?.some(
      (child) => child.id === serviceId
    );

    return (
      !!service.isExamAndCleaningService && (isServiceItself || isChildService)
    );
  });

export const adaptAvailability = ({
  practitioner,
  availableBlocks,
  date,
  serviceId,
  bookingServiceId,
  services,
  slotInterval,
}: {
  practitioner: IPractitionerOption;
  availableBlocks: ITimeSlot[];
  date: string;
  serviceId: string;
  bookingServiceId: string;
  services: IServiceOption[];
  slotInterval?: number;
}): FamilyTimeSlot => {
  const availableBlock = availableBlocks.find((block) => block.date === date);

  const searchingService: SearchingService = availableBlock?.searchingService
    ? availableBlock.searchingService
    : {
        serviceId,
        duration: 0,
        isSplitScheduling: false,
        hygienistDuration: 0,
        doctorDuration: 0,
      };

  const childServiceId = getChildServiceId({
    serviceId,
    bookingServiceId,
    services,
    searchingService,
  });

  const sortedBlocks = sortBy(availableBlock?.blocks);

  const blockValues = availableBlock?.isOptimizedApplied
    ? // OPTIMIZED ALREADY APPLIED ON BE
      sortedBlocks
    : getBlocksPerInterval(sortedBlocks, slotInterval);

  const blocks = blockValues.map((value) => ({
    value,
    services: [
      {
        serviceId,
        bookingServiceId,
        childServiceId,
        dentists: availableBlock?.dentists || [],
        availableBlockOperatories:
          availableBlock?.availableBlockOperatories || [],
        searchingService,
      },
    ],
  }));

  return {
    date,
    doctor: {
      id: practitioner.id,
      isActive: true,
      firstName: practitioner.firstName,
      lastName: practitioner.lastName,
      name: getPractitionerName(practitioner),
      slug: '',
      avatar: practitioner.avatar || null,
    },
    isClosed: !availableBlock,
    blocks,
  };
};

export const sortPractitionerSlots = ({
  slots,
  preferredPractitionerId,
}: {
  slots: FamilyTimeSlot[];
  preferredPractitionerId?: string | null;
}): FamilyTimeSlot[] =>
  [...slots].sort((first, second) => {
    if (preferredPractitionerId) {
      const isFirstPreferred = first.doctor.id === preferredPractitionerId;

      const isSecondPreferred = second.doctor.id === preferredPractitionerId;

      if (isFirstPreferred !== isSecondPreferred) {
        return isFirstPreferred ? -1 : 1;
      }
    }

    const nameOrder = first.doctor.name.localeCompare(second.doctor.name);

    if (nameOrder !== 0) return nameOrder;

    return first.doctor.id.localeCompare(second.doctor.id);
  });

export const isAvailabilityCurrent = ({
  availability,
  patientKey,
  date,
}: {
  availability: IResolvedAvailability | null;
  patientKey: string;
  date: string;
}) =>
  !!availability &&
  availability.patientKey === patientKey &&
  availability.date === date;

export const resolveSelectedDate = ({
  draftDates,
  activePatientKey,
  appointmentDate,
}: {
  draftDates: Record<string, string>;
  activePatientKey: string;
  appointmentDate?: string | null;
}) =>
  draftDates[activePatientKey] ||
  appointmentDate ||
  moment().format(DATE_FORMAT);

export const buildSearchWindow = ({
  selectedDate,
  monthOffset,
}: {
  selectedDate: string;
  monthOffset: number;
}): INextAvailabilityWindow => {
  const from = moment(selectedDate, DATE_FORMAT)
    .add(1, 'days')
    .add(monthOffset, 'months')
    .format(DATE_FORMAT);

  const to = moment(from, DATE_FORMAT)
    .add(1, 'months')
    .subtract(1, 'days')
    .format(DATE_FORMAT);

  return { from, to };
};

export const getSearchEndDate = ({
  selectedDate,
  startMonth,
  monthCount = NEXT_AVAILABILITY_SEARCH_MONTHS,
}: {
  selectedDate: string;
  startMonth: number;
  monthCount?: number;
}) =>
  buildSearchWindow({
    selectedDate,
    monthOffset: startMonth + monthCount - 1,
  }).to;
